import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { DateTime } from 'luxon';

import { emptyObject } from 'ui-components/default-props/empty';
import Global from '../../store/global-selectors';
import { calculateTargetDatetimesFromOperator } from './calculate-date-range-helper';
import DateRangeOptions, { customDateRangeOption } from './date-range-options';
import { useLuxonUserDateFormat, useSellerFormattedDate } from '../../hooks/use-datetime-formats';

const useDateRangeDate = ({ updateParent, localState, setLocalState, onApplyCallback, initialOption }) => {
  const { startDate, endDate } = localState;
  const [selectedOption, setSelectedOption] = useState(emptyObject);
  const sellerTimeZone = useSelector(Global.selectSellerTimeZoneIANA);
  const userDateFormat = useLuxonUserDateFormat();

  const localStartDateStr = useSellerFormattedDate(startDate);
  const localEndDateStr = useSellerFormattedDate(endDate);

  useEffect(() => {
    // If parent specified no dates, potential clear/reset. Unset selected option and specify default dates.
    const shouldReset = !startDate && !endDate;
    // If parent-specified dates and no selected option, populate selected option.
    const shouldUseCustomDate = !!startDate && !!endDate && selectedOption === emptyObject;
    if (shouldReset) {
      const todayString = DateTime.now().setZone(sellerTimeZone).toISO();
      setLocalState({ startDate: todayString, endDate: todayString });
      setSelectedOption({});
    } else if (shouldUseCustomDate) {
      setLocalState({
        startDate: DateTime.fromISO(startDate).toUTC().toISO(),
        endDate: DateTime.fromISO(endDate).toUTC().toISO(),
      });
      setSelectedOption(customDateRangeOption);
    }
  }, [endDate, selectedOption, sellerTimeZone, setLocalState, startDate]);

  useEffect(() => {
    if (initialOption && selectedOption === emptyObject) {
      const initialOptionObj = DateRangeOptions.find((option) => option.id === initialOption);
      if (initialOptionObj) {
        setSelectedOption(initialOptionObj);
      }
    }
  }, [initialOption, selectedOption]);

  const getSelectedDateRangeOption = useCallback(() => {
    return selectedOption;
  }, [selectedOption]);

  const getSelectedCustomDateRangeValues = useCallback(() => {
    const value = {
      startRangeDate: localStartDateStr,
      endRangeDate: localEndDateStr,
    };
    return { value };
  }, [localEndDateStr, localStartDateStr]);

  const onSelectedOptionChange = useCallback(
    (newOption) => {
      const updatedDateRange = calculateTargetDatetimesFromOperator(newOption.id, sellerTimeZone, startDate, endDate);
      setLocalState(updatedDateRange);
      setSelectedOption(newOption);
    },
    [endDate, sellerTimeZone, setLocalState, startDate],
  );

  const onSelectedValueChange = useCallback(
    ({ value }) => {
      const { startRangeDate, endRangeDate } = value;
      const newStartDate = DateTime.fromFormat(startRangeDate, userDateFormat, {
        zone: sellerTimeZone,
        setZone: true,
      })
        .startOf('day')
        .toUTC()
        .toISO();
      const newEndDate = DateTime.fromFormat(endRangeDate, userDateFormat, {
        zone: sellerTimeZone,
        setZone: true,
      })
        .endOf('day')
        .toUTC()
        .toISO();
      const newState = { startDate: newStartDate, endDate: newEndDate };
      setLocalState(newState);
    },
    [sellerTimeZone, setLocalState, userDateFormat],
  );

  const onRemoveClicked = useCallback(() => {
    updateParent({});
    setSelectedOption({});
  }, [updateParent]);

  const onHandleApplyClicked = useCallback(() => {
    // Call the original updateParent function
    updateParent();

    // Call the optional callback if it exists
    if (typeof onApplyCallback === 'function') {
      onApplyCallback(localState);
    }
  }, [updateParent, onApplyCallback, localState]);

  const actionHandlers = useMemo(() => {
    return {
      getCriterionOperator: getSelectedDateRangeOption,
      updateCriterionOperator: onSelectedOptionChange,
      getCriterionArgument: getSelectedCustomDateRangeValues,
      updateCriterionArgument: onSelectedValueChange,
    };
  }, [getSelectedCustomDateRangeValues, getSelectedDateRangeOption, onSelectedOptionChange, onSelectedValueChange]);

  return {
    actionHandlers,
    onRemoveClicked,
    onHandleApplyClicked,
    selectedOption,
  };
};

export default useDateRangeDate;
